import { getRecordActionItems } from '@/features/RecordActions/model/recordActionItems';
import { Menu } from 'primereact/menu';
import { useRef } from 'react';
import type { OperatorTaskDetailsResponse } from '@/core/generated/api';
import { useOperatorTasks } from '@/entities/OperatorTask/model/store';
import { TaskRelatedLinks } from '@/entities/OperatorTask/ui/TaskRelatedLinks';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { RequestErrorBanner, RequestLoading, RequestEmpty } from '@/shared/ui/RequestState';
import { RequestStatus } from '@/shared/model/requestStatus';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { formatMoscowDateTime } from '@/shared/lib/dateTime';
import buttonClasses from '@/styles/buttons.module.scss';
import classes from './OperatorTaskDetails.module.scss';

interface OperatorTaskDetailsProps {
    task: OperatorTaskDetailsResponse;
    onEdit: () => void;
    onStatusChange: (reopen: boolean) => void;
    busy: boolean;
}

/** Показывает задачу, её авторов и историю изменений */
export const OperatorTaskDetails = (
    { task, onEdit, onStatusChange, busy }: OperatorTaskDetailsProps
) => {
    const history = useOperatorTasks((state) => state.history);
    const historyCount = useOperatorTasks((state) => state.historyCount);
    const historyStatus = useOperatorTasks((state) => state.historyStatus);
    const historyError = useOperatorTasks((state) => state.historyError);
    const fetchHistory = useOperatorTasks((state) => state.fetchHistory);
    const canEdit = useCanAccessPermission('admin.operator-tasks.edit');
    const menu = useRef<Menu>(null);
    const completed = task.status?.id === 'completed';

    return (
        <div className={classes.details}>
            <div className={classes.toolbar}>
                <strong className={task.isOverdue
                    ? classes.overdue
                    : classes.status
                }>
                    {task.isOverdue
                        ? 'Просрочена'
                        : task.status?.name}
                </strong>
                {!completed && canEdit && (
                    <>
                        <Menu
                            ref={menu}
                            popup
                            model={getRecordActionItems({
                                kind: 'operatorTasks',
                                record: task
                            }, {
                                edit: onEdit
                            }, busy)}
                        />
                        <ActionButton
                            icon="pi pi-ellipsis-h"
                            iconOnly
                            text
                            className={buttonClasses.menuIcon}
                            aria-label="Действия задачи"
                            onClick={(event) => menu.current?.toggle(event)}
                        />
                    </>
                )}
            </div>
            <p className={classes.text}>{task.text}</p>
            <dl className={classes.facts}>
                <div><dt>Тип задачи</dt><dd>{task.type?.name}</dd></div>
                <div><dt>Срок</dt><dd>{formatMoscowDateTime(task.dueAtUtc)}</dd></div>
                <div>
                    <dt>Ответственный</dt>
                    <dd><span>{task.assignee?.login ?? 'Не назначен'}</span><small>{task.assignee?.role?.name}</small></dd>
                </div>
                <div><dt>Создала / создал</dt><dd>{task.createdBy?.name}<small>{formatMoscowDateTime(task.created)}</small></dd></div>
                <div><dt>Выполнила / выполнил</dt><dd>{task.completedBy?.name ?? '—'}{task.completedAtUtc &&
                    <small>{formatMoscowDateTime(task.completedAtUtc)}</small>
                }
                </dd></div>
            </dl>
            <section>
                <h3>Связанные объекты</h3>
                <TaskRelatedLinks context={task.context} />
            </section>
            <section>
                <h3>{completed
                    ? 'Напоминания остановлены'
                    : 'Ближайшее напоминание'}</h3>
                <p>{completed
                    ? 'После выполнения новые напоминания не отправляются'
                    : `${formatMoscowDateTime(task.nextReminder?.scheduledAtUtc)}`}</p>
            </section>
            <section>
                <h3>История задачи</h3>
                <RequestErrorBanner
                    status={historyStatus}
                    error={historyError}
                    fallbackMessage="Не удалось загрузить историю"
                    onRetry={() => task.id
                        ? fetchHistory(task.id, history.length > 0)
                        : undefined}
                />
                {historyStatus === RequestStatus.Loading &&
                    <RequestLoading message="Загрузка истории..." />
                }
                {historyStatus === RequestStatus.Success && !history.length &&
                    <RequestEmpty message="История пуста" />
                }
                <ol className={classes.history}>
                    {history.map((entry) => (
                        <li key={entry.id}>
                            <strong>{entry.action?.name}</strong>
                            <span>{entry.actor?.name} · {formatMoscowDateTime(entry.occurredAtUtc)}</span>
                            {entry.before?.assignee?.id !== entry.after?.assignee?.id &&
                                <small>Ответственный: {entry.before?.assignee?.login ?? 'Не назначен'} → {entry.after?.assignee?.login ?? 'Не назначен'}</small>
                            }
                            {entry.before?.dueAtUtc !== entry.after?.dueAtUtc && entry.before?.dueAtUtc &&
                                <small>Срок: {formatMoscowDateTime(entry.before.dueAtUtc)} → {formatMoscowDateTime(entry.after?.dueAtUtc)}</small>
                            }
                            {entry.before?.text !== entry.after?.text && entry.before?.text &&
                                <small>Описание: {entry.before.text} → {entry.after?.text}</small>
                            }
                            {entry.before?.type?.id !== entry.after?.type?.id && entry.before?.type &&
                                <small>Тип: {entry.before.type.name} → {entry.after?.type?.name}</small>
                            }
                            {entry.before && JSON.stringify(entry.before.reminders) !== JSON.stringify(entry.after?.reminders) &&
                                <small>Изменено расписание напоминаний</small>
                            }
                            {entry.before && JSON.stringify(entry.before.context) !== JSON.stringify(entry.after?.context) &&
                                <small>Изменены связанные объекты</small>
                            }
                        </li>
                    ))}
                </ol>
                {history.length < historyCount &&
                    <ActionButton
                        label="Показать ещё события"
                        outlined
                        disabled={historyStatus === RequestStatus.Loading}
                        onClick={() => task.id && void fetchHistory(task.id, true)}
                    />
                }
            </section>
            <ActionButton
                label={completed
                    ? 'Вернуть в работу'
                    : 'Выполнено'
                }
                icon={completed
                    ? 'pi pi-replay'
                    : 'pi pi-check'
                }
                permission={completed
                    ? 'admin.operator-tasks.reopen'
                    : 'admin.operator-tasks.complete'
                }
                loading={busy}
                disabled={busy || !task.version}
                onClick={() => onStatusChange(completed)}
            />
        </div>
    );
};
